import AsyncStorage from '@react-native-async-storage/async-storage';
import {showMessage} from 'react-native-flash-message';
import {logWebSocketConnection, logWebSocketDisconnection} from '../../utils/frontendLogger';
import {claimReceivingLabels} from '../../utils/receivingLabelRequest';
import {
  UPDATE_AWAITING_COUNT_FROM_IMPORT,
  CLEAR_IMPORT_AWAITING_COUNT,
  SCAN_SCORE_UPDATE,
  ON_DEMAND_IMPORT_EVENT,
  LOT_METADATA_REPORT_READY
} from '../../constants';
import {getSocket} from './socketClient';

let progress = 0;
let messageUrl = '';
let lastBarcodeId = null;

const MAX_BULK_MESSAGES = 3;

export const resetBulkProgress = () => {
  progress = 0;
};

export const TERMINAL_BULK_ACTION_STATUSES = ['completed', 'failed', 'cancelled'];

export const buildBulkActionUpdate = (msg, currentProgress = 0) => {
  if (msg?.type !== 'groove_bulk_actions') return null;

  const status = msg.data?.status;
  const identifier = msg.data?.identifier;
  const nextProgress = currentProgress >= 100 ? 50 : currentProgress;

  if (status === 'scheduled') {
    return {
      progress: nextProgress < 100 ? nextProgress + 10 : nextProgress,
      isTerminal: false
    };
  }

  if (status === 'in_progress') {
    return {
      progress: nextProgress < 100 ? nextProgress + 20 : nextProgress,
      isTerminal: false
    };
  }

  if (!TERMINAL_BULK_ACTION_STATUSES.includes(status)) return null;

  const payload = {
    source: 'groove_bulk_actions',
    status,
    identifier,
    activity: msg.data?.activity,
    messages: Array.isArray(msg.data?.messages) ? msg.data.messages : [],
    id: msg.data?.id
  };

  return {
    progress: 100,
    isTerminal: true,
    updateProducts: identifier === 'product',
    updateOrders: identifier !== 'product',
    orderUpdatePayload: identifier !== 'product' ? payload : null
  };
};

// GROOV-5067: groove_bulk_tags_actions carries its own 0-100 progress in
// msg.data (unlike groove_bulk_actions' {status, ...} shape), so it can feed
// the same module-level `progress` the disconnect handler reads — a tag job
// in flight must count as "in flight" too, or a disconnect mid-tag-update
// reads stale progress and skips the recovery refresh (GROOV-5067 defect 1).
export const buildBulkTagActionUpdate = msg => {
  if (msg?.type !== 'groove_bulk_tags_actions') return null;
  const tagProgress = typeof msg.data === 'number' ? msg.data : 0;
  return {progress: tagProgress, isTerminal: tagProgress === 100};
};

// GROOV-5067: decide what a socket disconnect should do to the bulk-action
// progress state.
//
// main.js only refreshes the orders grid when `progressCount >= 100 &&
// updateOrders`. The previous handler set progressCount to 0 AND updateOrders to
// true, which made that condition unreachable — so a socket drop mid-bulk-action
// left the grid rendering pre-action rows until a full page reload.
//
// A disconnect while an action is in flight (progress > 0) means the terminal
// 'completed' message may never arrive, so treat it as completion and let the grid
// re-read the server. An idle disconnect just resets progress; forcing a reload for
// every routine socket blip would cost a list fetch for no reason.
export const buildDisconnectRecovery = (currentProgress = 0) =>
  currentProgress > 0
    ? {progress: 100, forceOrdersRefresh: true}
    : {progress: 0, forceOrdersRefresh: false};

// groove_bulk_actions.messages is a plain text column, so the array arrives as its string form.
const parseBulkMessages = messages => {
  if (Array.isArray(messages)) {
    return messages;
  }
  if (typeof messages !== 'string' || !messages.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(messages);
    return Array.isArray(parsed) ? parsed : [messages];
  } catch (e) {
    return [messages];
  }
};

const showBulkActionResult = ({status, messages}) => {
  const unique = [...new Set(parseBulkMessages(messages).filter(Boolean))];
  const lines = unique.slice(0, MAX_BULK_MESSAGES);
  if (unique.length > MAX_BULK_MESSAGES) {
    lines.push(`+${unique.length - MAX_BULK_MESSAGES} more`);
  }
  const cancelled = status === 'cancelled';
  showMessage({
    message: cancelled ? 'Bulk action cancelled' : 'Bulk action failed',
    description: lines.join('\n') || undefined,
    type: cancelled ? 'info' : 'danger',
    autoHide: cancelled,
    duration: 6000
  });
};

const onDemandUserId = async () => {
  try {
    const info = await AsyncStorage.getItem('userInfo');
    return info ? String(JSON.parse(info)?.id ?? '') : '';
  } catch (error) {
    return '';
  }
};

const currentUsername = async () => {
  try {
    const info = await AsyncStorage.getItem('userInfo');
    return info ? JSON.parse(info)?.username ?? '' : '';
  } catch (error) {
    return '';
  }
};

export const createSocketHandlers = ({
  dispatch,
  getSettings,
  printlabel,
  logout,
  setMessage,
  setDimension,
  setProgressCount,
  setActionMsg,
  setProgressLabel,
  setUpdateOrders,
  setUpdateProducts,
  setBulkActionResult,
  setDashboardData
}) => {
  const dispatchOnDemandEvent = async (resp, kind) => {
    if (!resp) return;
    const userId = await onDemandUserId();
    if (!userId || String(resp.user_id) !== userId) return;
    dispatch({
      type: ON_DEMAND_IMPORT_EVENT,
      payload: {
        kind,
        message: resp.message,
        orderNo: resp.id ? String(resp.id) : null,
        receivedAt: Date.now()
      }
    });
  };

  const pnotif = async msg => {
    // TODO: Order Barcode Print // Change this for GROOV-2570
    const settings = getSettings();
    const msgDimension =
      msg.data?.dimensions?.split('x') ??
      (msg?.data?.print_type === 's2c_shipping_label' ? ['4', '6'] : '');
    if (msgDimension.length > 0) {
      setDimension({width: msgDimension[0], height: msgDimension[1]});
    }
    if (
      msg.type === 'generate_barcode_status' &&
      lastBarcodeId !== msg.data.id &&
      msg.data.url !== null
    ) {
      lastBarcodeId = msg.data.id;
      messageUrl = msg.data.url;
      if (String(msg.data.id) === (await AsyncStorage.getItem('recent_printed_barcode_id'))) {
        return;
      }
      await AsyncStorage.setItem('recent_printed_barcode_id', String(msg.data.id));
      if (settings?.print_packing_slips && msg?.data?.print_type === 'packing_slip') {
        printlabel(settings, msg);
      } else if (
        settings?.print_post_scanning_barcodes &&
        msg?.data?.print_type === 'order_barcode'
      ) {
        printlabel(settings, msg);
      } else if (
        settings?.print_ss_shipping_labels &&
        msg?.data?.print_type === 's2c_shipping_label'
      ) {
        printlabel(settings, msg);
      } else {
        window.open(messageUrl);
        return;
      }

      setTimeout(async () => {
        await AsyncStorage.removeItem('recent_printed_barcode_id');
      }, 3000);
    }
    if (msg?.data?.print_type === 'packing_slip') {
      const {total_orders, current_order_position, status} = msg.data;
      if (status === 'scheduled') {
        setProgressCount(4);
      }
      if (status === 'in_progress') {
        setProgressCount((current_order_position / total_orders) * 100);
      }
      if (status === 'completed') {
        setProgressCount(100);
        setUpdateOrders(true);
      }
    }
    if (msg.type === 'groove_bulk_actions') {
      const bulkActionUpdate = buildBulkActionUpdate(msg, progress);
      if (bulkActionUpdate) {
        progress = bulkActionUpdate.progress;
        setProgressCount(progress);
      }

      if (bulkActionUpdate?.isTerminal) {
        console.log('Message::::::', msg.data);

        if (bulkActionUpdate.updateProducts) {
          setUpdateProducts(true);
        }
        if (bulkActionUpdate.updateOrders) {
          setBulkActionResult(bulkActionUpdate.orderUpdatePayload);
          setUpdateOrders(true);
        }
        if (msg.data.status !== 'completed') {
          showBulkActionResult(msg.data);
        }

        if (msg.data.activity === 'status_update' && msg.data.identifier === 'orders') {
          await AsyncStorage.setItem('statusChange', 'completed');
        } else if (
          msg.data.activity === 'assign_rfo_orders' &&
          msg.data.identifier === 'orders'
        ) {
          await AsyncStorage.setItem('rfoAssignment', 'completed');
        }
      }
    }
    if (msg.type === 'groove_bulk_tags_actions') {
      const tagActionUpdate = buildBulkTagActionUpdate(msg);
      progress = tagActionUpdate.progress;
      setActionMsg('groove_bulk_tags_actions');
      setProgressLabel(msg.total ? `Tagging ${msg.completed} of ${msg.total} orders` : null);
      setProgressCount(progress);

      if (tagActionUpdate.isTerminal) {
        try {
          setBulkActionResult({
            source: 'groove_bulk_tags_actions',
            status: 'completed',
            identifier: 'orders',
            activity: 'tags_update',
            messages: []
          });
          setUpdateOrders(true);
          await AsyncStorage.setItem('progress', JSON.stringify(msg.data));
        } catch (error) {
          console.error('Error saving progress to AsyncStorage:', error);
        }
      }
    }
  };

  const barcodeLabel = async data => {
    if (!data?.url || data.username !== (await currentUsername())) return;
    showMessage({message: 'Barcode Labels generated successfully', type: 'success'});
    const settings = getSettings();
    if (settings?.print_product_barcode_labels) {
      const size = (await AsyncStorage.getItem('Product_Barcode_label_size')) || '3 x 1';
      const [width, height] = size.split('x').map(part => part.trim());
      setDimension({width, height});
      printlabel(settings, {
        data: {url: data.url, print_type: 'product_barcode', dimensions: `${width}x${height}`}
      });
    } else {
      window.open(data.url);
    }
  };

  const receivingLabel = url => {
    if (!url || !claimReceivingLabels()) return;
    showMessage({message: 'Labels generated Successfully', type: 'success'});
    window.open(url);
  };

  const importStatusUpdate = msg => {
    if (msg.import_info && msg.awaiting_orders_count !== undefined) {
      const {status} = msg.import_info;
      const awaitingCount = msg.awaiting_orders_count;

      if (status === 'in_progress' || status === 'fetch_api_response' || status === 'completed') {
        dispatch({type: UPDATE_AWAITING_COUNT_FROM_IMPORT, payload: awaitingCount});
      }

      if (status === 'completed' || status === 'failed' || status === 'cancelled') {
        setTimeout(() => {
          dispatch({type: CLEAR_IMPORT_AWAITING_COUNT});
        }, 5000);
      }
    }
  };

  return {
    connect: () => {
      console.log('Socket Connected');
      logWebSocketConnection(getSocket()?.id, {
        url: '#/scanandpack',
        socket_server: 'https://socket.groovepacker.com'
      });
    },
    disconnect: reason => {
      console.log('Socket Disconnected: ', reason);
      logWebSocketDisconnection(reason, getSocket()?.id);
      const recovery = buildDisconnectRecovery(progress);
      progress = recovery.progress;
      setProgressCount(progress);
      if (recovery.forceOrdersRefresh) {
        setUpdateOrders(true);
      }
    },
    connect_error: error => {
      console.log('Socket Connect Error: ', error);
    },
    popup_display_for_on_demand_import_v2: resp => {
      dispatchOnDemandEvent(resp, resp?.status === true ? 'ready' : 'not_found');
    },
    notification_ondemand_quickfix: resp => {
      dispatchOnDemandEvent(resp, 'quickfix');
    },
    ask_logout: msg => {
      console.log('Socket Checking Ask Logout');
      setMessage(msg.message);
    },
    hide_logout: () => {
      console.log('Socket Hide Ask Logout');
      setMessage('');
    },
    logout: msg => {
      if (msg.message === 'You have been logged out.') {
        console.log('Socket Logging Logout');
        logout();
      }
    },
    pnotif,
    barcode_lable: barcodeLabel,
    print_lable: receivingLabel,
    import_status_update: importStatusUpdate,
    dashboard_update_stat: response => {
      console.log('Received dashboard_update_stat:', response);
    },
    dashboard_update: message => {
      const filteredData = message.data.dashboard.filter(item => parseInt(item.duration, 10) === 30);
      setDashboardData(filteredData[0]);
    },
    scan_score_update: data => {
      dispatch({type: SCAN_SCORE_UPDATE, payload: data});
    },
    lot_metadata_report_ready: data => {
      dispatch({type: LOT_METADATA_REPORT_READY, payload: data});
    }
  };
};
